import React, { useMemo } from "react";
import { VegaLite } from "react-vega";
import { useElementSize } from "../../../hooks/useElementSize";
import {
  ProfileChartEmptyState,
  ProfileChartViewport,
} from "./DataStudioProfileChart.styles";
import type { DataStudioProfileChartProps } from "./DataStudioProfileChart.types";

const RESPONSIVE_SIZE_DEFAULTS = {
  width: "container",
  height: "container",
  autosize: { type: "fit", contains: "padding" },
};

/**
 * `width/height: "container"` is Vega-Lite's documented responsive mode, but
 * vega-embed only measures the container once at mount and only re-reads it
 * on a `window:resize` event. Inside a `<td>` (this profile grid's column
 * cells) that first read can land before table layout has settled, so the
 * chart measures 0×0 and never recovers — same failure mode ChartWidget.tsx's
 * `toResponsiveSpec` works around for its own charts. Measuring the viewport
 * via ResizeObserver and feeding back explicit pixel dimensions fixes it:
 * "container" stays only as the pre-measurement fallback for first paint.
 */
const withResponsiveSizing = (
  spec: Record<string, unknown>,
  widthPx: number,
  heightPx: number,
): Record<string, unknown> => {
  if (widthPx > 0 && heightPx > 0) {
    /**
     * A spec that already carries a numeric `height` (the ranked bar list,
     * the ring, the pie+legend) is a fixed-size widget by design — it chose
     * that height to control its own row/element spacing. Forcing the
     * *measured* container height onto it instead is actively harmful for the
     * bar list: if the cell's rendered height ever drifts even slightly above
     * its intended size (browsers only treat a `<td>`'s CSS `height` as a
     * minimum), the chart re-renders taller to fill that measurement, which
     * can grow the row further and feed an even bigger measurement next
     * tick — a resize/layout feedback loop. Only a spec that leaves `height`
     * unset is opting into filling whatever the container measures.
     */
    const ownHeight =
      typeof spec.height === "number" ? spec.height : heightPx;
    return {
      ...spec,
      width: widthPx,
      height: ownHeight,
      autosize: { type: "fit", contains: "padding" },
    };
  }
  return { ...RESPONSIVE_SIZE_DEFAULTS, ...spec };
};

export const DataStudioProfileChart: React.FC<DataStudioProfileChartProps> = ({
  column,
}) => {
  const accessibleLabel = `${column.name} distribution`;
  const [viewportRef, { width, height }] = useElementSize<HTMLDivElement>();
  const spec = useMemo(
    () =>
      column.visualSpec
        ? withResponsiveSizing(
            column.visualSpec,
            Math.floor(width),
            Math.floor(height),
          )
        : null,
    [column.visualSpec, width, height],
  );

  return (
    <ProfileChartViewport
      ref={viewportRef}
      role="img"
      aria-label={accessibleLabel}
      data-testid={`data-studio-profile-chart-${column.id}`}
    >
      {spec ? (
        <VegaLite spec={spec as any} actions={false} renderer="svg" tooltip />
      ) : (
        <ProfileChartEmptyState title="No distribution data for this column">
          No data
        </ProfileChartEmptyState>
      )}
    </ProfileChartViewport>
  );
};

export default DataStudioProfileChart;
